import { useCallback, useEffect, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { cn, toast } from '@clarix/ui';

/**
 * Copy `text` to the clipboard. Resolves false when the clipboard is
 * unavailable (non-secure context, older browser, jsdom in tests) so callers
 * can surface a failure instead of silently doing nothing.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (!navigator.clipboard) return false;
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export interface CopyButtonProps {
  /** Value to copy. Empty/null renders nothing. */
  value: string | null | undefined;
  /** Human label for the tooltip, aria-label, and toast — e.g. "email". */
  label?: string;
  className?: string;
  /** Icon size classes. Default `h-3 w-3` (table density). */
  iconSize?: string;
  /** How long the check stays after a copy, in ms. */
  confirmMs?: number;
  /** Toast on success. Off for dense grids where the icon flip is enough. */
  toastOnCopy?: boolean;
}

/**
 * Tiny copy-to-clipboard button — drop next to any inline value (email, phone,
 * id, URL). Shows the Lucide `Copy` icon and flips to `Check` for ~1.5s after a
 * successful copy; toasts on failure (and on success when `toastOnCopy`).
 *
 * Click is stopped from propagating so it never triggers the surrounding
 * row/card click. To reveal it only on hover, pass the group-hover classes:
 *   <span className="group/x …">{value}<CopyButton value={value}
 *     className="opacity-0 group-hover/x:opacity-100 focus-visible:opacity-100" /></span>
 */
export function CopyButton({
  value,
  label = 'value',
  className,
  iconSize = 'h-3 w-3',
  confirmMs = 1500,
  toastOnCopy = false,
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  // Reset the confirmed state after a beat so repeated copies still animate.
  // Cleanup keeps it from firing on an unmounted row (grids virtualise).
  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), confirmMs);
    return () => window.clearTimeout(t);
  }, [copied, confirmMs]);

  const onClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (!value) return;
      const ok = await copyToClipboard(value);
      if (ok) {
        setCopied(true);
        if (toastOnCopy) toast.success(`Copied ${label}`);
      } else {
        toast.error(`Could not copy the ${label}`);
      }
    },
    [label, toastOnCopy, value],
  );

  if (!value) return null;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={copied ? `Copied ${label}` : `Copy ${label}`}
      title={copied ? 'Copied!' : `Copy ${label}`}
      className={cn(
        'text-muted-foreground/70 hover:text-foreground hover:bg-accent focus-visible:ring-ring inline-flex h-5 w-5 shrink-0 items-center justify-center rounded transition-colors focus-visible:outline-none focus-visible:ring-1',
        className,
      )}
    >
      {copied ? (
        <Check className={cn(iconSize, 'text-success-fg')} strokeWidth={2} aria-hidden />
      ) : (
        <Copy className={iconSize} strokeWidth={1.75} aria-hidden />
      )}
    </button>
  );
}
